import React, { useState } from "react";
import { PACKAGES, PackageDef, TIER_LIMITS, TierLimit } from "./packages";
import PackageSetupWizard from "./PackageSetupWizard";

type Tab = "packages" | "tiers";

export default function Packages() {
  const [wizardPkg, setWizardPkg] = useState<PackageDef | null>(null);
  const [tab, setTab] = useState<Tab>("packages");

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Solution Packages</h1>
        <p className="text-gray-500 text-sm max-w-2xl">
          Every package is built on your existing Velaro platform — no new software, no migration.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-stone-700">
        {([["packages", "fa-box-open", "Solution Packages"], ["tiers", "fa-table", "Tier Limits & Competitor Analysis"]] as const).map(([id, icon, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === id
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}>
            <i className={`fa-solid ${icon} text-xs`} />
            {label}
          </button>
        ))}
      </div>

      {tab === "packages" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PACKAGES.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} onSetup={() => setWizardPkg(pkg)} />
            ))}
          </div>
          {wizardPkg && (
            <PackageSetupWizard pkg={wizardPkg} onClose={() => setWizardPkg(null)} />
          )}
        </>
      )}

      {tab === "tiers" && <TierComparisonTable />}
    </div>
  );
}

function TierComparisonTable() {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const TIER_COLS = [
    { key: "free" as const, label: "Free", color: "text-gray-600", bg: "bg-gray-50" },
    { key: "starter" as const, label: "Starter", color: "text-blue-600", bg: "bg-blue-50" },
    { key: "professional" as const, label: "Professional", color: "text-indigo-600", bg: "bg-indigo-50" },
    { key: "enterprise" as const, label: "Enterprise", color: "text-purple-600", bg: "bg-purple-50" }
  ];

  return (
    <div>
      <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        <strong>Internal use only.</strong> This table is for pricing/packaging decisions.
        <span className="ml-2 inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> = actively enforced in code.
          <span className="w-2 h-2 rounded-full bg-amber-400 inline-block ml-2" /> = stored only — needs enforcement sprint.
        </span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-stone-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-stone-800 border-b border-gray-200 dark:border-stone-700">
              <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-gray-300 w-48">Limit</th>
              {TIER_COLS.map(t => (
                <th key={t.key} className={`px-4 py-3 font-semibold text-center ${t.color} w-28`}>{t.label}</th>
              ))}
              <th className="text-left px-4 py-3 font-semibold text-gray-500 dark:text-gray-400">Competitor Benchmarks</th>
            </tr>
          </thead>
          <tbody>
            {TIER_LIMITS.map((row) => (
              <React.Fragment key={row.label}>
                <tr
                  className="border-b border-gray-100 dark:border-stone-700 hover:bg-gray-50 dark:hover:bg-stone-800 cursor-pointer transition-colors"
                  onClick={() => setExpandedRow(expandedRow === row.label ? null : row.label)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: row.enforced ? "#22c55e" : "#f59e0b" }}
                        title={row.enforced ? "Enforced in code" : "Stored only"}
                      />
                      <span className="font-medium text-gray-800 dark:text-gray-200">{row.label}</span>
                      <i className={`fa-solid fa-chevron-${expandedRow === row.label ? "up" : "down"} text-xs text-gray-400`} />
                    </div>
                  </td>
                  {TIER_COLS.map(t => (
                    <td key={t.key} className="px-4 py-3 text-center font-mono text-xs text-gray-700 dark:text-gray-300">
                      {row.tiers[t.key]}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {row.competitors.slice(0, 3).map((c, i) => (
                        <span key={i} className="text-xs bg-white dark:bg-stone-700 border border-gray-200 dark:border-stone-600 rounded px-2 py-0.5 text-gray-600 dark:text-gray-400">
                          <span className="font-medium text-gray-700 dark:text-gray-300">{c.name}:</span> {c.value}
                        </span>
                      ))}
                      {row.competitors.length > 3 && (
                        <span className="text-xs text-gray-400">+{row.competitors.length - 3} more</span>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedRow === row.label && (
                  <tr className="bg-blue-50 dark:bg-stone-900 border-b border-gray-200 dark:border-stone-700">
                    <td colSpan={6} className="px-4 py-3">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 max-w-3xl">{row.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {row.competitors.map((c, i) => (
                          <span key={i} className="text-xs bg-white dark:bg-stone-800 border border-gray-200 dark:border-stone-600 rounded px-2 py-1 text-gray-600 dark:text-gray-400">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">{c.name}:</span> {c.value}
                          </span>
                        ))}
                      </div>
                      {!row.enforced && (
                        <p className="mt-2 text-xs text-amber-700 font-medium">
                          ⚠️ Not enforced — this limit is stored in the DB but no code currently checks or blocks on it.
                          Add enforcement before selling this tier feature to customers.
                        </p>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PackageCard({ pkg, onSetup }: { pkg: PackageDef; onSetup(): void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-stone-700 overflow-hidden flex flex-col hover:shadow-md transition-shadow bg-white dark:bg-stone-800">
      {/* Colored top bar */}
      <div className="h-1.5" style={{ background: pkg.accentColor }} />

      {/* Card header */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: pkg.accentColor + "18" }}
          >
            <i className={`fa-solid ${pkg.icon} text-lg`} style={{ color: pkg.accentColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-base leading-tight">{pkg.name}</div>
            <div className="text-sm font-medium mt-0.5" style={{ color: pkg.accentColor }}>
              {pkg.tagline}
            </div>
          </div>
        </div>

        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          {pkg.description}
        </p>
      </div>

      {/* Feature list */}
      <div className="px-5 pb-4">
        <ul className={`space-y-1.5 ${!expanded && pkg.features.length > 4 ? "line-clamp-none" : ""}`}>
          {(expanded ? pkg.features : pkg.features.slice(0, 4)).map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <i className="fa-solid fa-circle-check text-xs mt-0.5 flex-shrink-0" style={{ color: pkg.accentColor }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {pkg.features.length > 4 && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="mt-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            {expanded ? "Show less" : `+${pkg.features.length - 4} more features`}
          </button>
        )}
      </div>

      {/* Setup steps teaser */}
      <div className="px-5 pb-4">
        <div className="flex items-center gap-1.5 flex-wrap">
          {pkg.wizardSteps.map((s, i) => (
            <React.Fragment key={i}>
              <span className="text-xs text-gray-400">{s.title}</span>
              {i < pkg.wizardSteps.length - 1 && (
                <i className="fa-solid fa-chevron-right text-gray-200 text-xs" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto px-5 pb-5 pt-2 border-t border-gray-100 dark:border-stone-700 flex items-center justify-between">
        <div className="text-xs text-gray-400">
          <i className="fa-solid fa-bolt mr-1" />
          {pkg.wizardSteps.length}-step setup · ~10 min
        </div>
        <button
          onClick={onSetup}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white transition-all hover:opacity-90 active:scale-95"
          style={{ background: pkg.accentColor }}
        >
          <i className="fa-solid fa-wand-magic-sparkles text-xs" />
          Set Up {pkg.name}
        </button>
      </div>
    </div>
  );
}
